import { PropsWithChildren, useRef, useState } from 'react';

import { useTranslation } from '@/lib/i18n/i18n.client';

import Badge from '@/decodo/components/atoms/badge';

import { findElClosestTheme } from '@/components/themeProviderUtils';
import {
  getSelectedTagProps,
  MAX_TAG_AMOUNT_BEFORE_FOLD,
  mutualBadgeProps,
  unSelectedTagProps,
} from '@/decodo/blocks/ScrapingTemplateLibrary/consts';
import CrossIcon from '~/icons/x.svg';

export const TagsFilter = ({
  tags,
  selectedTag,
  handleSelectTag,
}: PropsWithChildren<{
  tags: string[];
  selectedTag: string;
  handleSelectTag: (tag: string) => void;
}>) => {
  const { texts } = useTranslation();
  const ref = useRef<HTMLUListElement>(null);
  const [areTagsUnfolded, setAreTagsUnfolded] = useState<boolean>(false);

  const tagsToDisplay = areTagsUnfolded
    ? tags
    : tags.slice(0, MAX_TAG_AMOUNT_BEFORE_FOLD);

  const finalTags: { value: string; label: string }[] = [
    { value: '', label: texts.feature_library.all_tag },
    ...tagsToDisplay.map((tag) => {
      return {
        value: tag,
        label: tag,
      };
    }),
  ];

  const MoreTagsIcon = (() => {
    if (tags.length <= MAX_TAG_AMOUNT_BEFORE_FOLD) return null;

    if (areTagsUnfolded) {
      return (
        <div className='h-4 w-4 rotate-180 duration-300'>
          <CrossIcon className='' />
        </div>
      );
    }

    return '...';
  })();

  const theme = ref.current ? findElClosestTheme(ref.current) : 'light';

  return (
    <ul ref={ref} className='mb-2 flex w-full flex-1 flex-wrap gap-2'>
      {finalTags.map((tag) => (
        <li key={tag.value}>
          <Badge
            tabIndex={1}
            {...(selectedTag === tag.value
              ? getSelectedTagProps({ theme })
              : unSelectedTagProps)}
            onClick={() => {
              if (tag.value === selectedTag) {
                handleSelectTag('');
                return;
              }

              handleSelectTag(tag.value);
            }}
          >
            {tag.label}
          </Badge>
        </li>
      ))}
      {MoreTagsIcon && (
        <li>
          <Badge
            {...mutualBadgeProps}
            colors='brand-500'
            variant='outlined'
            tabIndex={0}
            onClick={() => setAreTagsUnfolded(!areTagsUnfolded)}
          >
            {MoreTagsIcon}
          </Badge>
        </li>
      )}
    </ul>
  );
};
